import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';
import {
  MentionCandidate,
  getActiveMentionQuery,
  getPlainMentionSpans,
  insertMentionAtRange,
  resolveMentionIdsFromText,
} from '@/shared/utils/mentions';

interface MentionTextareaProps {
  value: string;
  candidates: MentionCandidate[];
  onChange: (value: string, mentionIds: string[]) => void;
  placeholder?: string;
  rows?: number;
  readOnly?: boolean;
  className?: string;
  /** Associate an external <label htmlFor> with the textarea. */
  id?: string;
  'aria-label'?: string;
}

interface MentionQueryState {
  query: string;
  start: number;
  end: number;
}

const renderReadOnlyText = (value: string, mentionClassName: string, candidates: MentionCandidate[]) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of getPlainMentionSpans(value, candidates)) {
    const mention = value.slice(match.start, match.end);
    const mentionStart = match.start;

    parts.push(value.slice(lastIndex, mentionStart));
    parts.push(
      <span key={`${mentionStart}-${mention}`} className={mentionClassName}>
        {mention}
      </span>
    );
    lastIndex = mentionStart + mention.length;
  }

  parts.push(value.slice(lastIndex));
  return parts;
};

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  candidates,
  onChange,
  placeholder = 'Type @ to mention someone',
  rows = 3,
  readOnly = false,
  className,
  id,
  'aria-label': ariaLabel,
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [queryState, setQueryState] = useState<MentionQueryState | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredCandidates = useMemo(() => {
    if (!queryState) {
      return [];
    }
    const query = queryState.query.trim().toLowerCase();
    return candidates.filter((candidate) => {
      if (!query) return true;
      return (
        candidate.username.toLowerCase().includes(query) ||
        (candidate.email || '').toLowerCase().includes(query)
      );
    });
  }, [candidates, queryState]);

  useEffect(() => {
    setSelectedIndex((current) => {
      if (filteredCandidates.length === 0) return 0;
      return Math.min(current, filteredCandidates.length - 1);
    });
  }, [filteredCandidates]);

  const popupOpen = !readOnly && queryState !== null && filteredCandidates.length > 0;

  // Claim Escape above the hosting panel while the popup is open so the first
  // press closes only the popup (the manager stops Escape before onKeyDown).
  useModalEscape('mention-suggestions', () => setQueryState(null), {
    priority: 400,
    enabled: popupOpen,
  });

  const syncQueryState = () => {
    if (!textareaRef.current || readOnly) {
      setQueryState(null);
      return;
    }
    const nextQueryState = getActiveMentionQuery(
      textareaRef.current.value,
      textareaRef.current.selectionStart || 0
    );
    setQueryState(nextQueryState);
  };

  const emitChange = (nextValue: string) => {
    onChange(nextValue, resolveMentionIdsFromText(nextValue, candidates));
  };

  const selectCandidate = (candidate: MentionCandidate) => {
    if (!textareaRef.current || !queryState) return;
    const replacement = insertMentionAtRange(value, queryState, candidate.username);
    emitChange(replacement.value);
    setQueryState(null);
    window.requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.selectionStart = replacement.cursorIndex;
      textareaRef.current.selectionEnd = replacement.cursorIndex;
    });
  };

  if (readOnly) {
    return (
      <div
        className={cn(
          'min-h-[88px] w-full whitespace-pre-wrap rounded-lg border p-3 text-sm',
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary,
          className
        )}
      >
        {value?.trim()
          ? renderReadOnlyText(
              value,
              cn('inline-flex rounded-md px-1.5 py-0.5 font-medium', theme.bgTertiary, theme.textPrimary),
              candidates,
            )
          : t('No description')}
      </div>
    );
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        id={id}
        aria-label={ariaLabel}
        value={value || ''}
        onChange={(event) => {
          emitChange(event.target.value);
          window.requestAnimationFrame(syncQueryState);
        }}
        onClick={syncQueryState}
        onKeyUp={syncQueryState}
        onBlur={() => window.setTimeout(() => setQueryState(null), 120)}
        onFocus={syncQueryState}
        onKeyDown={(event) => {
          if (!queryState || filteredCandidates.length === 0) {
            return;
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex((current) => (current + 1) % filteredCandidates.length);
            return;
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex((current) =>
              current === 0 ? filteredCandidates.length - 1 : current - 1
            );
            return;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            selectCandidate(filteredCandidates[selectedIndex]);
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setQueryState(null);
          }
        }}
        rows={rows}
        placeholder={placeholder}
        className={cn(
          'w-full resize-none rounded-lg border p-3 transition-colors',
          theme.bgSecondary,
          theme.borderPrimary,
          theme.textPrimary,
          theme.focusRing,
          className
        )}
      />

      {popupOpen ? (
        <div
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-y-auto rounded-lg border shadow-lg',
            theme.bgPrimary,
            theme.borderPrimary
          )}
        >
          {filteredCandidates.map((candidate, index) => (
            <button
              key={candidate.id}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                selectCandidate(candidate);
              }}
              className={cn(
                'flex w-full items-start justify-between gap-3 px-3 py-2 text-left text-sm',
                index === selectedIndex ? theme.hover : '',
                theme.textPrimary
              )}
            >
              <span className="font-medium">@{candidate.username}</span>
              {candidate.email ? (
                <span className={cn('truncate text-xs', theme.textMuted)}>{candidate.email}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
